import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import SearchRounded from '@mui/icons-material/SearchRounded';
import NavLockedBubble from '../leftnav/NavLockedBubble';
import PriceTaxTotalSummary from '../non-rx-catalog/PriceTaxTotalSummary';
import NonRxProductImageDialog from './NonRxProductImageDialog';
import { roundMoney } from '../../utils/nonRxTax';
import './NonRxCart.css';

const formatPrice = (price) => {
  const value = parseFloat(price);
  return `$${(Number.isNaN(value) ? 0 : value).toFixed(2)}`;
};

const isDraftNonRxItem = (item) =>
  !!(item && (item.isDraft || String(item.id || '').startsWith('nonrx-')));

const isCustomNonRxItem = (item) =>
  !!(item && (item.is_custom === true || item.is_custom === 'true'));

const isCatalogLinkedItem = (item) =>
  !!(item && item.non_rx_product_id);

const CatalogPopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ zIndex: 9999, width: props.anchorEl ? props.anchorEl.clientWidth : undefined }}
    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
  />
);

CatalogPopper.propTypes = {
  anchorEl: PropTypes.object,
};

const MetaField = ({ label, children, className }) => (
  <div
    className={
      'assign-non-rx-meta-field' + (className ? ` ${className}` : '')
    }
  >
    <span className="assign-non-rx-meta-label">{label}</span>
    <div className="assign-non-rx-meta-control">{children}</div>
  </div>
);

MetaField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

MetaField.defaultProps = {
  className: '',
};

const CatalogItemSearchField = ({
  index,
  item,
  disabled,
  t,
  onSearchCatalog,
  onSelectCatalogProduct,
  onChangeItem,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(item.item_number || '');
  const searchTimerRef = useRef(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    setInputValue(item.item_number || '');
  }, [item.item_number, item.non_rx_product_id]);

  useEffect(
    () => () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    },
    []
  );

  const runSearch = (query, { immediate = false } = {}) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    if (!onSearchCatalog) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const trimmed = String(query || '').trim();
    const execute = async () => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        const results = await onSearchCatalog(trimmed);
        if (seq === requestSeqRef.current) {
          setOptions(Array.isArray(results) ? results : []);
        }
      } catch (error) {
        if (seq === requestSeqRef.current) {
          setOptions([]);
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    };
    if (immediate) {
      execute();
      return;
    }
    searchTimerRef.current = setTimeout(execute, 300);
  };

  if (isCustomNonRxItem(item) && !isCatalogLinkedItem(item)) {
    const descriptionLabel = t('Assign.NonRxCart.Description', {
      defaultValue: 'Description',
    });
    return (
      <div className="assign-non-rx-custom-fields">
        <MetaField
          label={t('Assign.NonRxCart.ItemNumber', { defaultValue: 'Item #' })}
        >
          <TextField
            size="small"
            fullWidth
            variant="standard"
            value={item.item_number || ''}
            disabled={disabled}
            onChange={(event) =>
              onChangeItem(index, 'item_number', event.target.value)
            }
            inputProps={{ maxLength: 100 }}
            placeholder={t('Assign.NonRxCart.ItemNumberPlaceholder', {
              defaultValue: 'SKU or internal item number',
            })}
          />
        </MetaField>
        <MetaField label={`${descriptionLabel} *`}>
          <TextField
            size="small"
            fullWidth
            variant="standard"
            required
            value={item.description || ''}
            disabled={disabled}
            onChange={(event) =>
              onChangeItem(index, 'description', event.target.value)
            }
            inputProps={{ maxLength: 255 }}
            placeholder={t('Assign.NonRxCart.DescriptionPlaceholder', {
              defaultValue: 'Product name or short description',
            })}
          />
        </MetaField>
        <MetaField
          label={t('Assign.NonRxCart.Upc', { defaultValue: 'UPC / Barcode' })}
        >
          <TextField
            size="small"
            fullWidth
            variant="standard"
            value={item.upc || ''}
            disabled={disabled}
            onChange={(event) => onChangeItem(index, 'upc', event.target.value)}
            helperText={t('Assign.NonRxCart.UpcHint', {
              defaultValue: 'Optional barcode for this one-off item',
            })}
            inputProps={{ maxLength: 32 }}
            placeholder={t('Assign.NonRxCart.UpcPlaceholder', {
              defaultValue: 'Scan or type barcode',
            })}
          />
        </MetaField>
      </div>
    );
  }

  if (isCatalogLinkedItem(item) || disabled) {
    return (
      <div className="assign-non-rx-product-display">
        <span className="assign-non-rx-product-name">
          {item.item_number || '—'}
        </span>
        {item.description ? (
          <span className="assign-non-rx-product-desc">{item.description}</span>
        ) : null}
        {item.upc ? (
          <span className="assign-non-rx-product-upc">UPC {item.upc}</span>
        ) : null}
      </div>
    );
  }

  return (
    <Autocomplete
      size="small"
      freeSolo={false}
      clearOnBlur={false}
      openOnFocus
      fullWidth
      options={options}
      loading={loading}
      inputValue={inputValue}
      PopperComponent={CatalogPopper}
      onInputChange={(event, value, reason) => {
        if (reason === 'reset') return;
        setInputValue(value);
        onChangeItem(index, 'item_number', value);
        runSearch(value);
      }}
      onOpen={() => runSearch(inputValue, { immediate: true })}
      onChange={(event, product) => {
        if (product && onSelectCatalogProduct) {
          onSelectCatalogProduct(index, product);
        }
      }}
      getOptionLabel={(option) => {
        if (!option || typeof option !== 'object') return '';
        return [option.item_number, option.description].filter(Boolean).join(' — ');
      }}
      isOptionEqualToValue={(option, value) =>
        option && value && String(option.id) === String(value.id)
      }
      filterOptions={(opts) => opts}
      renderOption={(props, option) => (
        <li {...props} key={option.id} className="assign-non-rx-catalog-option">
          <div className="assign-non-rx-catalog-option-body">
            <span className="assign-non-rx-catalog-option-item">
              {option.item_number}
            </span>
            {option.description ? (
              <span className="assign-non-rx-catalog-option-desc">
                {option.description}
              </span>
            ) : null}
            {option.upc ? (
              <span className="assign-non-rx-catalog-option-upc">
                UPC {option.upc}
              </span>
            ) : null}
          </div>
        </li>
      )}
      noOptionsText={t('Assign.NonRxCart.SearchNoResults', {
        defaultValue: 'No catalog items found',
      })}
      loadingText={t('Assign.NonRxCart.Searching', {
        defaultValue: 'Searching…',
      })}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          className="assign-non-rx-search-field"
          placeholder={t('Assign.NonRxCart.SearchItem', {
            defaultValue: 'Search catalog by item #, UPC, or name',
          })}
          InputProps={{
            ...params.InputProps,
            disableUnderline: false,
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 16, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

CatalogItemSearchField.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  t: PropTypes.func.isRequired,
  onSearchCatalog: PropTypes.func,
  onSelectCatalogProduct: PropTypes.func,
  onChangeItem: PropTypes.func.isRequired,
};

const ProductImageCell = ({
  imageUrl,
  canUpload,
  t,
  onOpenUpload,
}) => {
  const thumbContent = imageUrl ? (
    <img src={imageUrl} alt="" />
  ) : (
    <Inventory2Outlined />
  );

  return (
    <div className="assign-non-rx-thumb-cell">
      {canUpload ? (
        <React.Fragment>
          <button
            type="button"
            className="assign-non-rx-thumb assign-non-rx-thumb--clickable"
            onClick={onOpenUpload}
            aria-label={t('Assign.NonRxCart.ImageUploadAria', {
              defaultValue: 'Upload product photo',
            })}
          >
            {thumbContent}
          </button>
          <NavLockedBubble
            placement="right"
            featureTitle={t('Assign.NonRxCart.ImageDialogTitle', {
              defaultValue: 'Product photo',
            })}
            description={t('Assign.NonRxCart.ImageGuidance', {
              defaultValue:
                'Click the photo to upload or update this product in your Marketplace catalog. Photos are optional but help patients identify items on Deliver.',
            })}
            hint={t('Assign.NonRxCart.ImageGuidanceHint', {
              defaultValue:
                'Saved photos are stored in your Marketplace catalog for future orders.',
            })}
          />
        </React.Fragment>
      ) : (
        <div
          className="assign-non-rx-thumb assign-non-rx-thumb--readonly"
          aria-hidden={!imageUrl}
        >
          {thumbContent}
        </div>
      )}
    </div>
  );
};

ProductImageCell.propTypes = {
  imageUrl: PropTypes.string,
  canUpload: PropTypes.bool,
  t: PropTypes.func.isRequired,
  onOpenUpload: PropTypes.func.isRequired,
};

ProductImageCell.defaultProps = {
  imageUrl: '',
  canUpload: false,
};

const NonRxCart = ({
  t,
  items,
  disabled,
  allowAdd,
  priceEditable,
  editingPriceIndex,
  onAddItem,
  onAddCustomItem,
  onChangeItem,
  onRemoveItem,
  onStartPriceEdit,
  onSavePriceEdit,
  onSaveDraftItem,
  onSearchCatalog,
  onSelectCatalogProduct,
  onSaveProductImage,
}) => {
  const rowData = Array.isArray(items) ? items : [];
  const isEmpty = rowData.length === 0;
  const canAdd = allowAdd !== false && !disabled;
  const [imageDialogIndex, setImageDialogIndex] = useState(null);
  const [imageSaving, setImageSaving] = useState(false);
  const imageDialogItem =
    imageDialogIndex != null ? rowData[imageDialogIndex] : null;
  const cartTotals = rowData.reduce(
    (acc, item) => {
      const qty = Math.max(1, parseFloat(item && item.qty) || 1);
      const unitPrice = roundMoney(item && item.price);
      const unitTax = roundMoney(item && item.tax_amount);
      return {
        price: roundMoney(acc.price + unitPrice * qty),
        tax: roundMoney(acc.tax + unitTax * qty),
      };
    },
    { price: 0, tax: 0 }
  );
  const showCartTotals =
    !isEmpty && (cartTotals.price > 0 || cartTotals.tax > 0);

  useEffect(() => {
    if (disabled && imageDialogIndex != null && !imageSaving) {
      setImageDialogIndex(null);
    }
  }, [disabled, imageDialogIndex, imageSaving]);

  const closeImageDialog = () => {
    if (imageSaving) {
      return;
    }
    setImageDialogIndex(null);
  };

  const handleImageSave = async (imageFile) => {
    if (
      imageDialogIndex == null ||
      !onSaveProductImage ||
      !imageFile ||
      imageSaving ||
      disabled
    ) {
      return;
    }
    const dialogItem = rowData[imageDialogIndex];
    const dialogRowLocked = disabled && dialogItem && !isDraftNonRxItem(dialogItem);
    if (dialogRowLocked) {
      return;
    }
    setImageSaving(true);
    try {
      const saved = await onSaveProductImage(imageDialogIndex, imageFile);
      if (saved) {
        setImageDialogIndex(null);
      }
    } finally {
      setImageSaving(false);
    }
  };

  return (
    <Box className="assign-non-rx-cart">
      <Box className="assign-non-rx-cart-toolbar">
        <Typography className="assign-non-rx-cart-title" variant="subtitle2">
          {t('Assign.NonRxCart.Title', { defaultValue: 'Marketplace Items' })}
          {!isEmpty ? (
            <span className="assign-non-rx-cart-count">{rowData.length}</span>
          ) : null}
        </Typography>
        <div className="assign-non-rx-cart-actions">
        <Button
          size="small"
          color="primary"
          variant="outlined"
          className="assign-non-rx-cart-add-btn"
          startIcon={<AddCircleOutlineRounded fontSize="small" />}
          onClick={onAddItem}
          disabled={!canAdd}
        >
          {t('Assign.NonRxCart.AddFromCatalog', {
            defaultValue: 'Add from catalog',
          })}
        </Button>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          className="assign-non-rx-cart-oneoff-btn"
          startIcon={<AddCircleOutlineRounded fontSize="small" />}
          onClick={onAddCustomItem}
          disabled={!canAdd}
        >
          {t('Assign.NonRxCart.AddOneOff', {
            defaultValue: 'Add one-off item',
          })}
        </Button>
        </div>
      </Box>

      <div
        className={
          'assign-rx-cart-scroll assign-non-rx-list-shell' +
          (isEmpty ? ' is-empty' : '')
        }
      >
        {isEmpty ? (
          <div className="assign-non-rx-empty">
            <Inventory2Outlined className="assign-non-rx-empty-icon" />
            <p>{t('Assign.NonRxCart.Empty', { defaultValue: 'No Marketplace items added' })}</p>
            <span>
              {t('Assign.NonRxCart.EmptyHint', {
                defaultValue:
                  'Scan UPC above, search catalog, or add a one-off item',
              })}
            </span>
          </div>
        ) : (
          <div className="assign-non-rx-list">
            {rowData.map((item, index) => {
              const isDraft = isDraftNonRxItem(item);
              const catalogLinked = isCatalogLinkedItem(item);
              const rowLocked = disabled && !isDraft;
              const fieldsLocked = rowLocked || catalogLinked;
              const isEditingPrice =
                !isDraft && priceEditable && editingPriceIndex === index;
              const showAssignedPriceControls = rowLocked && priceEditable;

              return (
                <article className="assign-non-rx-item" key={item.id || index}>
                  <div className="assign-non-rx-item-main">
                    <ProductImageCell
                      imageUrl={item.image_url}
                      canUpload={!!onSaveProductImage && !rowLocked}
                      t={t}
                      onOpenUpload={() => setImageDialogIndex(index)}
                    />
                    <div className="assign-non-rx-item-body">
                      <CatalogItemSearchField
                        index={index}
                        item={item}
                        disabled={rowLocked}
                        t={t}
                        onSearchCatalog={onSearchCatalog}
                        onSelectCatalogProduct={onSelectCatalogProduct}
                        onChangeItem={onChangeItem}
                      />
                    </div>
                    <div className="assign-non-rx-item-actions">
                      {isDraft && disabled && (
                        <Tooltip
                          title={t('Assign.NonRxCart.SaveToOrder', {
                            defaultValue: 'Save to order',
                          })}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onSaveDraftItem(index)}
                            aria-label={t('Assign.NonRxCart.SaveToOrder', {
                              defaultValue: 'Save to order',
                            })}
                          >
                            <SaveOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={t('Assign.NonRxCart.Remove', {
                          defaultValue: 'Remove',
                        })}
                      >
                        <IconButton
                          size="small"
                          className="assign-non-rx-remove-btn"
                          onClick={() => onRemoveItem(index)}
                          disabled={rowLocked}
                          aria-label={t('Assign.NonRxCart.Remove', {
                            defaultValue: 'Remove',
                          })}
                        >
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="assign-non-rx-item-meta">
                    <div className="assign-non-rx-meta-left">
                      <MetaField label={t('Assign.NonRxCart.Uom', { defaultValue: 'UOM' })}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="standard"
                          value={item.uom || ''}
                          disabled={fieldsLocked}
                          onChange={(event) =>
                            onChangeItem(index, 'uom', event.target.value)
                          }
                          inputProps={{ maxLength: 10 }}
                        />
                      </MetaField>

                      <MetaField label={t('Assign.NonRxCart.Qty', { defaultValue: 'Qty' })}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="standard"
                          type="number"
                          value={item.qty === 0 || item.qty ? item.qty : ''}
                          disabled={rowLocked}
                          onChange={(event) =>
                            onChangeItem(index, 'qty', event.target.value)
                          }
                          inputProps={{ min: 1, step: '1' }}
                        />
                      </MetaField>
                      <MetaField label={t('NonRxCatalog.RequirePayment', { defaultValue: 'Payment' })}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              color="primary"
                              checked={item.require_payment === true || item.require_payment === 'true'}
                              disabled={rowLocked}
                              onChange={(event) =>
                                onChangeItem(index, 'require_payment', event.target.checked)
                              }
                            />
                          }
                          label={
                            item.require_payment === true || item.require_payment === 'true'
                              ? t('NonRxCatalog.PaymentOn', { defaultValue: 'Required' })
                              : t('NonRxCatalog.PaymentOff', { defaultValue: 'Skipped' })
                          }
                        />
                      </MetaField>
                    </div>

                    <div className="assign-non-rx-meta-right">
                      <MetaField label={t('Assign.NonRxCart.Price', { defaultValue: 'Price' })}>
                        {showAssignedPriceControls ? (
                          isEditingPrice ? (
                            <Box className="assign-non-rx-inline-edit">
                              <TextField
                                size="small"
                                variant="standard"
                                type="number"
                                value={
                                  item.price === 0 || item.price ? item.price : ''
                                }
                                onChange={(event) =>
                                  onChangeItem(index, 'price', event.target.value)
                                }
                                inputProps={{ min: 0, step: '0.01' }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onSavePriceEdit(index)}
                                aria-label={t('Assign.SaveTooltip', { defaultValue: 'Save' })}
                              >
                                <SaveOutlined fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box className="assign-non-rx-inline-edit">
                              <span className="assign-non-rx-price-text">
                                {formatPrice(item.price)}
                              </span>
                              <IconButton
                                size="small"
                                onClick={() => onStartPriceEdit(index)}
                                aria-label={t('Assign.EditTooltip', { defaultValue: 'Edit' })}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Box>
                          )
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            variant="standard"
                            type="number"
                            value={item.price === 0 || item.price ? item.price : ''}
                            disabled={fieldsLocked && !priceEditable}
                            onChange={(event) =>
                              onChangeItem(index, 'price', event.target.value)
                            }
                            inputProps={{ min: 0, step: '0.01' }}
                          />
                        )}
                      </MetaField>

                      <MetaField label={t('Assign.NonRxCart.Tax', { defaultValue: 'Tax' })}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="standard"
                          type="number"
                          value={
                            item.tax_amount === 0 || item.tax_amount
                              ? item.tax_amount
                              : ''
                          }
                          disabled={fieldsLocked}
                          onChange={(event) =>
                            onChangeItem(index, 'tax_amount', event.target.value)
                          }
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </MetaField>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
      {showCartTotals ? (
        <div className="assign-non-rx-cart-totals">
          <span className="assign-non-rx-cart-totals-label">
            {t('Assign.NonRxCart.CartTotals', {
              defaultValue: 'Marketplace total',
            })}
          </span>
          <PriceTaxTotalSummary
            t={t}
            price={cartTotals.price}
            tax={cartTotals.tax}
          />
        </div>
      ) : null}
      <NonRxProductImageDialog
        open={imageDialogIndex != null && !disabled}
        item={imageDialogItem}
        t={t}
        saving={imageSaving}
        onClose={closeImageDialog}
        onSave={handleImageSave}
      />
    </Box>
  );
};

NonRxCart.propTypes = {
  t: PropTypes.func.isRequired,
  items: PropTypes.array,
  disabled: PropTypes.bool,
  allowAdd: PropTypes.bool,
  priceEditable: PropTypes.bool,
  editingPriceIndex: PropTypes.number,
  onAddItem: PropTypes.func.isRequired,
  onAddCustomItem: PropTypes.func,
  onChangeItem: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onStartPriceEdit: PropTypes.func,
  onSavePriceEdit: PropTypes.func,
  onSaveDraftItem: PropTypes.func,
  onSearchCatalog: PropTypes.func,
  onSelectCatalogProduct: PropTypes.func,
  onSaveProductImage: PropTypes.func,
};

NonRxCart.defaultProps = {
  items: [],
  disabled: false,
  allowAdd: true,
  onAddCustomItem: () => {},
  priceEditable: false,
  editingPriceIndex: null,
  onStartPriceEdit: () => {},
  onSavePriceEdit: () => {},
  onSaveDraftItem: () => {},
  onSearchCatalog: null,
  onSelectCatalogProduct: null,
  onSaveProductImage: null,
};

export default NonRxCart;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/NonRxCart.jsx